// ? This card should match the ForumTopicCard in Forum/ForumTopicCard as much as possible
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  mdiAlertCircleCheckOutline,
  mdiAlertCircleOutline,
  mdiOpenInNew,
  mdiReply,
  mdiReplyOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  Campaign,
  CampaignOutlined,
  CommentOutlined,
  ContentCopyOutlined,
  DeleteForeverOutlined,
  Edit,
  EditOutlined,
  PushPin,
  PushPinOutlined,
  Reply,
  ReplyOutlined,
} from '@mui/icons-material';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  ListItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tinymce/tinymce-react';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import ReactPlayer from 'react-player';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getChildThreads,
  getContentUrl,
  useBlockUser,
  useCreateFeedThread,
  useDeleteThread,
  useGetChildThreads,
  useLikeUnlikeThread,
  useUpdateThread,
} from '@juno/client-api';
import {
  CommunityUser,
  SearchContentTypeEnum,
  StatusD4cEnum,
  Tag,
  Thread,
  ThreadBody,
} from '@juno/client-api/model';
import { ANALYTICS_CONFIGURATION } from '@juno/constants';
import {
  ConfirmDeleteDialog,
  DrawerHeader,
  MediaPreview,
  RightExpandableDrawer,
  UserQuickView,
  WithOptionalTooltip,
  customShadow,
} from '@juno/ui';
import {
  MutationAction,
  getContentRoute,
  getTimeSinceCreated,
  handleFileNewTab,
  onMutation,
  snackOptions,
  truncateFileName,
  useGetPresignedUrl,
  useScrollIntoView,
  useSettings,
} from '@juno/utils';
import { FeedContext } from '../../Feed';
import CollapsibleMenu from '../CollapsibleMenu';
import { CommentReply } from '../CommentReply';
import CommentsRepliesSection from '../CommentsRepliesSection';
import LittleHeads from '../LittleHeads';
import ThreadEditor from '../ThreadEditor';
import { TimeSinceCreatedText } from '../styles';
import './styles.css';

const MAX_CONTENT_HEIGHT = 200;

interface PostCardProps {
  thread: Thread;
  autoScroll?: boolean;
  autoScrollTarget?: string;
  loadReplies?: boolean;
  isInForum: boolean;
  refetch: () => void;
  handlePinUnpin: (thread: Thread) => Promise<void>;
  handleTagFilter?: (tag: Tag) => void;
  handleSelect?: (thread: Thread) => void;
  setForumData?: React.Dispatch<React.SetStateAction<any[]>>;
  userCanInteract: boolean;
}
const PostCard: React.FC<PostCardProps> = ({
  thread,
  autoScroll = false,
  autoScrollTarget,
  loadReplies = false,
  isInForum,
  refetch,
  handlePinUnpin,
  handleTagFilter,
  handleSelect,
  setForumData,
  userCanInteract,
}) => {
  const { isSmallScreen, setReportContent, reportContent, myContentReports, site, user, sitePath } =
    useSettings();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [viewReplies, setViewReplies] = useState(loadReplies);
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [postHeight, setPostHeight] = useState<string | number>(MAX_CONTENT_HEIGHT);
  const contentRef = useRef<HTMLElement | null>(null);
  const [userQuickViewAnchorEl, setUserQuickViewAnchorEl] = useState<null | HTMLElement>(null);
  const [userQuickViewUserId, setUserQuickViewUserId] = useState<string>('');
  const navigateRoute = useNavigate();
  const feedContext = useContext(FeedContext);
  const location = useLocation();
  const theme = useTheme();

  const {
    author,
    parent_thread_id,
    body,
    image,
    date_created,
    liked_users,
    num_likes,
    num_replies,
    downloads,
    tags,
    reply_authors,
  } = thread;
  const isReply = !!parent_thread_id;
  const isAuthor = author?.id === user?.id;

  const likeUnlike = useLikeUnlikeThread(onMutation(MutationAction.UPDATE, '', refetch));
  const threadUpdate = useUpdateThread(onMutation(MutationAction.UPDATE, '', refetch));
  const threadDelete = useDeleteThread(onMutation(MutationAction.DELETE, 'Post', refetch));

  const postRef = useRef<HTMLDivElement | null>(null);
  const quickViewRef = useRef(null);

  const handleCopyLink = async () => {
    if (!navigator.clipboard) {
      console.error('Clipboard API not supported');
      return;
    }

    const urlResponse = await getContentUrl(
      site?.id || '',
      thread.id,
      location.pathname.split('/').pop() || '',
    );
    const locationSplit = window.location.href.split('/');
    locationSplit.pop();
    const locationHref = locationSplit.join('/');
    const contentUrl = `${locationHref}${urlResponse.url}`;

    navigator.clipboard
      .writeText(contentUrl)
      .then(() => {
        enqueueSnackbar('Copied!', snackOptions('success'));
      })
      .catch((error) => {
        enqueueSnackbar('Copy Failed!', snackOptions('error'));
        console.error('Failed to copy URL to clipboard:', error);
      });
  };

  const { presignedUrl, isLoadingPresignedUrl } = useGetPresignedUrl(image);

  const clickHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');

    if (link) {
      e.stopPropagation();
      return;
    }

    const userId = target.getAttribute('data-mention-id');
    if (userId) {
      e.stopPropagation();
      setUserQuickViewAnchorEl(target);
      setUserQuickViewUserId(userId);
    }
  };

  const onDeleteConfirm = async () => {
    await threadDelete.mutateAsync({ siteId: site?.id || '', threadId: thread.id });
    setForumData &&
      setForumData((old) => {
        return old.map((f) => {
          if (f.feed.id === thread.parent_id) {
            return {
              ...f,
              feed: {
                ...f.feed,
                post_count: f.feed.post_count - 1,
              },
            };
          }
          return f;
        });
      });
  };

  const onMakeAnnouncment = async () => {
    try {
      await threadUpdate.mutateAsync({
        siteId: site?.id || '',
        threadId: thread.id,
        data: {
          ...thread,
          announcement: !thread.announcement,
          date_announced: new Date().toISOString(),
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = () => {
    if (isAuthor) {
      setShowDeleteConfirm(true);
    } else {
      setReportContent({
        contentBeingReported: {
          ...reportContent.contentBeingReported,
          id: thread.id,
          type: isReply ? 'Comment' : 'Post',
          reporter: user?.id || '',
          author: author?.id || '',
        },
        reportContentDialogOpen: true,
        adminDelete: true,
        deletionCallback: () => refetch(),
      });
    }
  };

  const handleLikeButtonClick = async () => {
    // the server handles whether the user has liked/disliked
    // split on the sitePath/ to grab the page slug and the possible discussion id
    const pageSlug = location.pathname.split(`${sitePath}/`).pop() || '';
    await likeUnlike.mutateAsync({
      siteId: site?.id || '',
      threadId: thread.id,
      data: {
        ...thread,
        metadata: {
          page_slug: pageSlug,
        },
      },
    });
  };

  const { data: threadReplies, refetch: refetchThreadReplies } = useGetChildThreads(
    site?.id || '',
    thread.id,
    {
      order: 'date_created',
    },
    {
      query: {
        enabled: viewReplies,
      },
    },
  );

  const { highlight } = useScrollIntoView(postRef, {
    enabled: autoScroll && !!autoScrollTarget && autoScrollTarget === thread.id,
    behavior: 'smooth',
  });

  useEffect(() => {
    const container = contentRef.current;
    const content = container?.querySelector('.content') as HTMLElement | null;

    if (container && content) {
      setPostHeight(content.scrollHeight);
    }
  }, [isExpanded, body]);

  const userLiked = useMemo(() => {
    return liked_users?.filter((u) => {
      return u.id === user?.id;
    });
  }, [liked_users, user?.id]);

  if (!thread) {
    return <Skeleton variant='rectangular' height={200} />;
  }

  return (
    <>
      <RightExpandableDrawer open={isEditing} onClose={() => setIsEditing(false)}>
        <DrawerHeader title={`Edit ${isReply ? 'Comment' : 'Post'}`} />

        <ThreadEditor
          threadToEdit={thread}
          handleUpdate={async (updatedThread: Thread) => {
            const data = { ...thread, ...updatedThread };
            const newThread = await threadUpdate.mutateAsync({
              siteId: site?.id || '',
              data: data,
              threadId: thread.id,
            });
            setIsEditing(false);
          }}
          handleCancel={() => setIsEditing(false)}
          showActionMenu={isReply ? false : true}
          showToolbar={isReply ? false : true}
          refetch={refetch}
        />
      </RightExpandableDrawer>

      <motion.div>
        <Card
          sx={{
            p: 2,
            boxShadow: highlight
              ? `0 0 0 2px ${theme.palette.primary.main}, 0 4px 16px 0 ${theme.palette.primary.main}CC`
              : 'none',
            transition: 'box-shadow 0.2s ease-in-out',
          }}
          ref={postRef}
        >
          <Box
            display={'flex'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            position={'relative'}
          >
            {author?.active ? (
              <>
                <Avatar
                  src={author?.icon || author?.avatar || ''}
                  alt={`${author?.first_name} ${author?.last_name}`}
                  ref={quickViewRef}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    setUserQuickViewAnchorEl(quickViewRef.current);
                    setUserQuickViewUserId(author?.id || '');
                  }}
                />
                <Stack
                  spacing={0}
                  ml={2}
                  onClick={(e) => {
                    setUserQuickViewAnchorEl(quickViewRef.current);
                    setUserQuickViewUserId(author?.id || '');
                  }}
                >
                  <Typography
                    variant='subtitle2'
                    fontWeight={'bold'}
                    style={{ cursor: 'pointer' }}
                  >{`${author?.first_name} ${author?.last_name}`}</Typography>
                  <TimeSinceCreatedText
                    variant='subtitle2'
                    fontWeight={'normal'}
                    fontSize={'0.6rem'}
                  >
                    {getTimeSinceCreated(isSmallScreen || false, date_created)}
                  </TimeSinceCreatedText>
                </Stack>
              </>
            ) : (
              <Tooltip title='Deactivated User' placement='top-start'>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'not-allowed',
                  }}
                >
                  <Avatar
                    src={author?.icon || author?.avatar || ''}
                    alt={`${author?.first_name} ${author?.last_name}`}
                    ref={quickViewRef}
                    sx={{
                      opacity: '0.3',
                    }}
                    onClick={(e) => {}}
                  />
                  <Stack spacing={0} ml={2} onClick={(e) => {}}>
                    <Typography
                      variant='subtitle2'
                      fontWeight={'bold'}
                      sx={{
                        opacity: '0.3',
                      }}
                    >{`${author?.first_name} ${author?.last_name}`}</Typography>
                    <TimeSinceCreatedText
                      variant='subtitle2'
                      fontWeight={'normal'}
                      fontSize={'0.6rem'}
                      sx={{ opacity: 1 }}
                    >
                      {getTimeSinceCreated(isSmallScreen || false, date_created)}
                    </TimeSinceCreatedText>
                  </Stack>
                </span>
              </Tooltip>
            )}

            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            >
              <CollapsibleMenu maxVisibleIcons={0}>
                {!isReply && (
                  <Tooltip title={'Comment'} arrow>
                    <span>
                      <IconButton
                        disabled={!userCanInteract}
                        onClick={() => {
                          setShowComments(true);
                        }}
                      >
                        <CommentOutlined />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {feedContext?.iAmAdmin && !isReply && (
                  <Tooltip title={thread.pinned ? 'Unpin' : 'Pin'} arrow>
                    <IconButton onClick={() => handlePinUnpin(thread)}>
                      {thread.pinned ? (
                        <PushPin
                          sx={{
                            transform: 'rotate(45deg)',
                            color: (theme) => theme.palette.primary.main,
                          }}
                        />
                      ) : (
                        <PushPinOutlined sx={{ transform: 'rotate(45deg)' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                {feedContext?.iAmAdmin && !isReply && (
                  <Tooltip
                    title={
                      thread.announcement === true ? 'Remove Announcement' : 'Make Announcement'
                    }
                    arrow
                  >
                    <IconButton onClick={onMakeAnnouncment}>
                      {thread.announcement === true ? (
                        <Campaign
                          sx={{
                            color: (theme) => theme.palette.primary.main,
                          }}
                        />
                      ) : (
                        <CampaignOutlined />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title='Copy Link' arrow>
                  <IconButton onClick={() => handleCopyLink()}>
                    <ContentCopyOutlined />
                  </IconButton>
                </Tooltip>
                {isAuthor && (
                  <Tooltip title='Edit' arrow>
                    <IconButton onClick={() => setIsEditing(!isEditing)}>
                      {isEditing ? <EditOutlined /> : <Edit />}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    myContentReports.find((r) => r.object_id === thread.id)
                      ? 'Already Reported'
                      : isReply
                      ? 'Report Comment'
                      : 'Report Post'
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      disabled={
                        !!myContentReports.find(
                          (r) =>
                            r.object_id === thread.id &&
                            r.status !== StatusD4cEnum.Restored &&
                            r.status !== StatusD4cEnum.Ignored,
                        )
                      }
                      onClick={() => {
                        setReportContent({
                          contentBeingReported: {
                            ...reportContent.contentBeingReported,
                            id: thread.id,
                            type: isReply ? 'Comment' : 'Post',
                            reporter: user?.id || '',
                            author: thread.author?.id || '',
                          },
                          reportContentDialogOpen: true,
                        });
                      }}
                    >
                      {myContentReports.find(
                        (r) =>
                          r.object_id === thread.id &&
                          r.status !== StatusD4cEnum.Restored &&
                          r.status !== StatusD4cEnum.Ignored,
                      ) ? (
                        <Icon path={mdiAlertCircleCheckOutline} size={1} />
                      ) : (
                        <Icon path={mdiAlertCircleOutline} size={1} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {isAuthor ? (
                  <Tooltip title='Delete' arrow>
                    <IconButton onClick={handleDelete}>
                      <DeleteForeverOutlined />
                    </IconButton>
                  </Tooltip>
                ) : (
                  feedContext?.iAmAdmin && (
                    <Tooltip title='Delete as Admin' arrow>
                      <IconButton onClick={handleDelete}>
                        <DeleteForeverOutlined />
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </CollapsibleMenu>
            </Box>
          </Box>
          {thread.title && (
            <Typography
              variant='h6'
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                textOverflow: 'ellipsis',
                maxWidth: 'calc(100% - 40px)',
              }}
            >
              {thread.title}
            </Typography>
          )}
          <Box
            ref={contentRef}
            sx={{
              position: 'relative',
              mt: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: '#f4f4f4',
            }}
          >
            <Box
              maxHeight={
                isExpanded || isReply
                  ? 'auto'
                  : tags && tags.length > 0
                  ? MAX_CONTENT_HEIGHT + 32
                  : MAX_CONTENT_HEIGHT
              }
              overflow={'hidden'}
              textOverflow={'ellipsis'}
            >
              <div
                className='content'
                onClick={clickHandler}
                dangerouslySetInnerHTML={{ __html: body as unknown as string }}
              />
              {thread.comment_media_type && thread.comment_media && (
                <MediaPreview
                  type={thread.comment_media_type}
                  url={thread.comment_media || ''}
                  gifId={thread.comment_media || ''}
                />
              )}
            </Box>
            {(typeof postHeight === 'string' || postHeight >= MAX_CONTENT_HEIGHT) && !isReply && (
              <Grid container alignItems='center' justifyContent='space-between'>
                <Grid item>
                  <Button
                    size='small'
                    variant='text'
                    aria-label={isExpanded ? 'See Less' : 'See More'}
                    color='primary'
                    endIcon={isExpanded ? <ArrowUpwardOutlined /> : <ArrowDownwardOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded((prevExpanded) => !prevExpanded);
                    }}
                  >
                    {isExpanded ? (
                      <Typography color='primary' variant='caption' fontWeight='regular'>
                        See Less
                      </Typography>
                    ) : (
                      <Typography color='primary' variant='caption' fontWeight='regular'>
                        See More
                      </Typography>
                    )}
                  </Button>
                </Grid>
              </Grid>
            )}
            {image && (
              <Box
                display={'flex'}
                justifyContent={'center'}
                sx={{ borderRadius: 2, overflow: 'hidden' }}
                pb={1}
              >
                {image.search('cloudinary') >= 0 ? (
                  <img
                    alt={thread.title ? `${thread.title} - image` : 'Post image'}
                    style={{ width: '100%' }}
                    src={image}
                    loading='lazy'
                  />
                ) : isLoadingPresignedUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px',
                    }}
                  >
                    <Skeleton variant='rectangular' height={200} />
                  </Box>
                ) : (
                  <video
                    preload='metadata'
                    muted
                    style={{ maxHeight: '400px', maxWidth: '100%' }}
                    src={presignedUrl}
                    controls
                  />
                )}
              </Box>
            )}
            {thread.post_video && thread.post_video.length > 0 && (
              <Box pb={1}>
                <ReactPlayer url={thread.post_video} controls={true} width={'100%'} />
              </Box>
            )}
            {tags && tags?.length > 0 && (
              <Box pb={1}>
                {tags.map((t) => (
                  <Tooltip key={t.id} title={`Filter by "${t.value}"`} arrow>
                    <Chip
                      key={t.id}
                      label={t.value}
                      sx={{
                        mr: 1,
                        p: '2px 6px',
                        boxShadow: customShadow,
                        color: 'inherit',
                        bgcolor: 'inherit',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTagFilter && handleTagFilter(t);
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
            {downloads && downloads?.length > 0 && (
              <Box pb={1}>
                <ListItem disablePadding>
                  <Grid container spacing={1}>
                    {downloads.map((file) => {
                      return (
                        <Grid item key={file.title}>
                          <Card
                            variant='outlined'
                            sx={{
                              justifyContent: 'space-between',
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                            }}
                          >
                            <Tooltip title={`Open ${file.title}`} placement='top'>
                              <Typography
                                component='span'
                                sx={{
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  // Open in a new tab. If browser can't render, it'll download.
                                  handleFileNewTab(file.url);
                                }}
                              >
                                {truncateFileName(file.title)}
                                <Icon
                                  path={mdiOpenInNew}
                                  size={0.7}
                                  style={{ marginLeft: '4px' }}
                                />
                              </Typography>
                            </Tooltip>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </ListItem>
              </Box>
            )}
            <UserQuickView
              anchorEl={userQuickViewAnchorEl}
              userId={userQuickViewUserId}
              open={Boolean(userQuickViewAnchorEl)}
              setAnchorEl={setUserQuickViewAnchorEl}
              onViewProfile={(userId: string): void => {
                navigateRoute(getContentRoute(site?.id || '', userId, SearchContentTypeEnum.user));
              }}
              onMessageUser={function (userId: string): void {
                throw new Error('Function not implemented.');
              }}
            />
          </Box>

          <Box display='flex' justifyContent='space-between' alignItems='center' mt={1}>
            <Grid container alignItems='center' justifyContent='space-between'>
              <Grid item>
                {isReply && num_replies > 0 && (
                  <Button
                    size='small'
                    variant='text'
                    aria-label={viewReplies ? 'Hide Replies' : 'See Replies'}
                    color='primary'
                    endIcon={viewReplies ? <ArrowUpwardOutlined /> : <ArrowDownwardOutlined />}
                    onClick={() => {
                      setViewReplies((prev) => !prev);
                    }}
                  >
                    {viewReplies ? (
                      <Typography color='primary' variant='caption' fontWeight='regular'>
                        Hide Replies
                      </Typography>
                    ) : (
                      <Typography color='primary' variant='caption' fontWeight='regular'>
                        See Replies
                      </Typography>
                    )}
                  </Button>
                )}
              </Grid>

              <Grid item>
                <Box display='flex' alignItems='center'>
                  {tags && tags.length > 0 && (
                    <Box display='flex' alignItems='center' mr={2}>
                      <LittleHeads items={[tags.length]} />
                      <Typography fontSize={14} ml={1}>
                        {tags.length === 1 ? 'Tag' : 'Tags'}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    size='small'
                    disabled={!userCanInteract}
                    color={userLiked && userLiked?.length > 0 ? 'primary' : 'inherit'}
                    aria-label='like post'
                    onClick={handleLikeButtonClick}
                    startIcon={
                      userLiked && userLiked?.length > 0 ? (
                        <FavoriteRoundedIcon color='primary' sx={{ width: 24, height: 24 }} />
                      ) : (
                        <FavoriteBorderOutlinedIcon sx={{ width: 24, height: 24 }} />
                      )
                    }
                  >
                    {num_likes}
                  </Button>

                  {isReply && (
                    <WithOptionalTooltip title='Reply' arrow show={num_replies > 0}>
                      <Button
                        size='small'
                        color={num_replies > 0 ? 'primary' : 'inherit'}
                        onClick={() => {
                          setViewReplies(false);
                          feedContext?.setThreadToReplyTo(thread);
                        }}
                        startIcon={
                          num_replies > 0 ? (
                            <Reply color='primary' sx={{ width: 24, height: 24 }} />
                          ) : (
                            <ReplyOutlined sx={{ width: 24, height: 24, color: '#525252' }} />
                          )
                        }
                      >
                        {num_replies > 0 ? (
                          num_replies
                        ) : (
                          <Typography
                            variant='caption'
                            color={num_replies > 0 ? 'primary' : '#525252'}
                            fontWeight='semibold'
                          >
                            Reply
                          </Typography>
                        )}
                      </Button>
                    </WithOptionalTooltip>
                  )}

                  {!isReply && (
                    <Button
                      disabled={!userCanInteract}
                      sx={{ ml: 1, cursor: 'pointer' }}
                      onClick={() => {
                        handleSelect?.(thread);
                        setShowComments((prev) => !prev);
                      }}
                    >
                      <LittleHeads
                        items={reply_authors as CommunityUser[]}
                        totalItems={num_replies}
                      />
                      <Typography
                        fontSize={14}
                        ml={1}
                        sx={{
                          color:
                            num_replies > 0
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                        }}
                      >
                        {num_replies === 1 ? 'Comment' : 'Comments'}
                      </Typography>
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {isReply && <Divider sx={{ mx: 1, mt: 2 }} />}
          {viewReplies && threadReplies && (
            <Stack
              spacing={1}
              ml={1}
              mt={1}
              p={0}
              pl={1}
              sx={{
                borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              {threadReplies
                .sort((a: Thread, b: Thread) => {
                  return (
                    new Date(b.date_created ?? '').getTime() -
                    new Date(a.date_created ?? '').getTime()
                  );
                })
                .map((reply, index) => {
                  return (
                    <Box position={'relative'} key={reply.id}>
                      <PostCard
                        userCanInteract={userCanInteract}
                        handleSelect={() => {
                          feedContext?.setThreadToReplyTo(reply);
                        }}
                        handlePinUnpin={() => handlePinUnpin(reply)}
                        refetch={() => {
                          refetchThreadReplies();
                          refetch();
                        }}
                        thread={reply}
                        autoScroll
                        autoScrollTarget={autoScrollTarget}
                        isInForum={isInForum}
                      />
                    </Box>
                  );
                })}
            </Stack>
          )}

          {feedContext?.threadToReplyTo?.id === thread.id && (
            <CommentReply
              thread={thread}
              isInForum={isInForum}
              refetch={() => {
                refetch();
                setViewReplies(true);
              }}
            />
          )}

          {showComments && (
            <CommentsRepliesSection
              userCanInteract={userCanInteract}
              thread={thread}
              refetch={refetch}
              isInForum={isInForum}
              handlePinUnpin={handlePinUnpin}
              setShowComments={setShowComments}
            />
          )}
        </Card>
        {showDeleteConfirm && (
          <ConfirmDeleteDialog
            handleClose={() => setShowDeleteConfirm(false)}
            handleDelete={() => {
              onDeleteConfirm();
              setShowDeleteConfirm(false);
            }}
            title={`Delete ${isReply ? 'Comment' : 'Post'}?`}
            message={
              <Typography variant='subtitle1'>
                Are you sure you want to delete this {isReply ? 'comment' : 'post'}?
              </Typography>
            }
            subText={
              <Typography color='error' variant='body2' sx={{ mt: 1 }}>
                {isReply
                  ? 'This includes any replies within the comment.'
                  : 'This includes any comments and replies within the post.'}
              </Typography>
            }
          />
        )}
      </motion.div>

      {/* <RightExpandableDrawer
        open={!!commentingThreadId}
        onClose={() => {
          setCommentingThreadId('');
          editorRef.current?.setContent('');
        }}
      >
        <DrawerHeader title='Add Comment' />
        <Grid
          container
          spacing={0}
          px={1}
          py={2}
          boxShadow={customShadow}
          width='100%'
          sx={{
            ml: 'auto',
            backgroundColor: '#f4f4f4',
            boxShadow: customShadow,
            borderRadius: '8px',
          }}
        >
          <Grid
            item
            xs={1}
            display={'flex'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            flexDirection={'column'}
          >
            <Avatar
              sx={{ mr: 0 }}
              src={user?.avatar || user?.icon || ''}
              alt={`${user?.first_name} ${user?.last_name}`}
            />
          </Grid>
          <Grid item xs={11}>
            <Stack>
              <Box ml={1}>
                <Editor
                  apiKey={process.env.NX_TINY_MCE_API_KEY}
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  initialValue={''}
                  init={{
                    convert_urls: false,
                    contextmenu: false,
                    max_height: 600,
                    auto_focus: true,
                    highlight_on_focus: false,
                    placeholder: 'Add Comment',
                    content_css: '/styles.css',
                    content_style: `.user_mention{ color: ${theme.palette.primary.main};, cursor: pointer; }`,
                    mentions_selector: 'span.user_mention',
                    mentions_menu_complete: (editor: any, userInfo: any) => {
                      userMentions.current = [...userMentions.current, userInfo];
                      const span = createTinyMceMentionSpan(editor, userInfo);
                      return span;
                    },
                    mentions_fetch: (query: any, success: any) => {
                      mentions_fetch(query, success, getImageSrc, platform?.id || '');
                    },
                    mentions_item_type: 'profile',
                    height: 250,
                    width: '100%',
                    setup: (editor) => {
                      editor.setProgressState(true);
                    },
                    toolbar: false,
                    menubar: false,
                    statusbar: false,
                    skin: 'material-outline',
                  }}
                  plugins={'autolink emoticons link mentions'}
                />
              </Box>
              <Box display='flex' justifyContent='space-between' gap={2} mt={3} alignItems='center'>
                <LoadingButton
                  fullWidth
                  sx={{
                    ml: 2,
                  }}
                  aria-label='Post comment'
                  variant='contained'
                  onClick={async () => {
                    // double check that the user mentions are in the body
                    const taggedUsers = userMentions.current?.filter((m) =>
                      editorRef.current?.getContent().includes(m.id),
                    );
                    const pageSlug = location.pathname.split('/').pop() || '';
                    const data = {
                      body: editorRef.current?.getContent() as unknown as ThreadBody,
                      author_id: user?.id,
                      parent_thread_id: commentingThreadId,
                      tagged_users: taggedUsers,
                    } as Thread;
                    const newThread = await threadCreate.mutateAsync({
                      siteId: site?.id || '',
                      feedId: thread.id,
                      data: {
                        ...data,
                        metadata: {
                          page_slug: pageSlug,
                        },
                      },
                    });
                    firehoseActivity(
                      ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
                      thread?.parent_id || '',
                      null,
                      `${user?.id || ''}`,
                      isInForum
                        ? ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM
                        : ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FEED,
                      commentingThreadId === thread.id
                        ? ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_COMMENT.value
                        : ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_REPLY.value,
                      '',
                      null,
                    );
                    setCommentingThreadId('');
                    editorRef.current?.setContent('');
                  }}
                >
                  Comment
                </LoadingButton>
                <Button
                  fullWidth
                  variant='outlined'
                  onClick={() => {
                    setCommentingThreadId('');
                    editorRef.current?.setContent('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </RightExpandableDrawer> */}
    </>
  );
};
export default PostCard;
